import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { setSideBarColors } from "@/containers/Theme/ThemeHelper";
import agentClient from "@apis/agentClient";
import biClient from "@apis/businessIntelligenceClient";
import channelClient from "@apis/channelClient";
import sitemapAPI from "@apis/sitemap";
import type { EnrolledAgent } from "@apis/agentClient";
import useAvailibleMenu from "./useAvailibleMenu";
import { MenuTypeConst, type MenuType, type MenuRoute, type AvailibleMenuData } from "./menuTypes";
import type { RootState } from "@/types/RootState";
import type { LoginInfo } from "@/types/LoginInfo";
import type { Channel } from "@apis/channelClient/channelClient.dto";
import type { SiteMapItem } from "@/cleanup/pages/AllSiteMaps/types";
import type { DashboardMenuItem } from "@apis/businessIntelligenceClient/businessIntelligenceClient.dto";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@hooks/useFeatures";
import { UserPermission } from "@/types/generated/UserPermission";
import { hasPermission } from "@hooks/usePermissions";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@hooks/useLanguage";

/**
 * Extended DashboardMenuItem with menu-specific fields
 * These fields may be returned by the API but aren't in the base DTO
 */
interface ExtendedDashboardMenuItem extends DashboardMenuItem {
  menuName?: string;
  powerBiReportType?: string;
}

/**
 * Extended Channel with menu-specific fields
 * These fields may be returned by the API but aren't in the base DTO
 */
interface ExtendedChannel extends Channel {
  displayName?: string;
  showInHomeMenu?: boolean;
  homeMenuItemName?: string;
}

/**
 * Extended SiteMapItem with menu-specific fields
 * These fields may be returned by the API but aren't in the base DTO
 */
interface ExtendedSiteMapItem extends SiteMapItem {
  showInHomeMenu?: boolean;
  homeMenuItemName?: string;
}

/**
 * Extended menu route with additional category and type fields
 */
interface ExtendedMenuRoute extends MenuRoute {
  category?: string;
  type?: string;
  reportType?: string;
  channelId?: number;
  siteMapId?: number;
  excludeSubPages?: string[];
  title?: string;
}

/**
 * Return type of useHomeMenu hook
 */
interface HomeMenuData extends AvailibleMenuData {
  type: MenuType;
  loading: boolean;
  routes: ExtendedMenuRoute[];
}

/**
 * Hook for managing the home menu routes based on user permissions and features
 *
 * @param loginInfo - Current user's login information including features and permissions
 * @param onMenuChange - Optional callback function triggered when menu is updated
 * @returns Menu data including routes, available paths, and loading state
 */
const useHomeMenu = (
  loginInfo: LoginInfo,
  onMenuChange: () => void = () => {}
): HomeMenuData => {
  const global = useSelector((state: RootState) => state.all);
  const { custumInfo, organisation } = loginInfo;

  const initialRoutes: ExtendedMenuRoute[] = [
    {
      view:
        hasFeature(FeatureKey.utilization_suez) ||
        (hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_HOME_DASHBOARD)),
      collapse: true,
      name: translate(LanguageKey.UserPermission_MENU_HOME_DASHBOARD_description_key),
      state: "openDashboards",
      icon: <span className="dashboard-menu-icon menu-icon"></span>,
      views: [],
    },
    {
      collapse: true,
      name: translate(LanguageKey.assets_key),
      state: "openAssets",
      icon: <span className="assets-menu-icon menu-icon"></span>,
      views: [
        {
          view: hasFeature(FeatureKey.loc_map) && hasPermission(UserPermission.MENU_HOME_ASSET_MAP),
          to: "/assets_location",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_ASSET_MAP_description_key),
        },
        {
          view: hasPermission(UserPermission.MENU_HOME_ASSET_LIST),
          to: "/tracked_assets",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_ASSET_LIST_description_key),
          subPages: ["/tracked_assets_update"],
        },
        {
          view:
            hasFeature(FeatureKey.asset_browser) &&
            hasFeature(FeatureKey.loc_map) &&
            hasPermission(UserPermission.MENU_HOME_ASSET_MAP),
          to: "/asset_browser",
          layout: "",
          name: translate(LanguageKey.sidebar_asset_browser_key),
        },
        {
          view: hasPermission(UserPermission.MENU_HOME_TAGS),
          to: "/asset_tags_manage_admin",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_TAGS_description_key),
        },
        {
          view: hasPermission(UserPermission.MENU_HOME_ASSET_TYPES),
          to: "/asset_types_overview",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_ASSET_TYPES_description_key),
        },
        {
          view: hasPermission(UserPermission.MANUFACTURING),
          to: "/manufacturing_info_view",
          layout: "",
          name: translate(LanguageKey.manufacturing_history_key),
        },
        {
          view:
            hasFeature(FeatureKey.location_history) &&
            hasPermission(UserPermission.MENU_HOME_LOCATION_HISTORY),
          to: "/location_history",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_LOCATION_HISTORY_description_key),
        },
      ],
    },
    {
      view: hasFeature(FeatureKey.agents) && hasPermission(UserPermission.MENU_HOME_AGENT),
      collapse: true,
      name: translate(LanguageKey.agents_section_title_key),
      state: "openAgents",
      icon: <span className="agents-menu-icon menu-icon"></span>,
      views: [],
    },
    {
      collapse: true,
      name: translate(LanguageKey.site_maps_key),
      state: "openSiteMaps",
      icon: <span className="site-maps-menu-icon menu-icon"></span>,
      views: [
        {
          view:
            hasFeature(FeatureKey.loc_site_map) && hasPermission(UserPermission.MENU_HOME_SITE_MAP),
          to: "/all_site_maps",
          layout: "",
          name: translate(LanguageKey.all_site_maps_key),
          subPages: ["/site_map_view"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.geozones_key),
      state: "openGeozone",
      icon: <span className="geozones-menu-icon menu-icon"></span>,
      views: [
        {
          view:
            hasFeature(FeatureKey.geozone_db) && hasPermission(UserPermission.MENU_HOME_GEOZONE_OVERVIEW),
          to: "/geozones_dashboard",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_GEOZONE_OVERVIEW_description_key),
        },
        {
          view:
            hasFeature(FeatureKey.geozone_list) && hasPermission(UserPermission.MENU_HOME_GEOZONE_LIST),
          to: "/geozones_list",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_GEOZONE_LIST_description_key),
          subPages: ["/geozone_individual"],
        },
        {
          view:
            hasFeature(FeatureKey.geozone_taglist) &&
            hasPermission(UserPermission.MENU_HOME_GEOZONE_TAGS),
          to: "/geozones_tag_list",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_GEOZONE_TAGS_description_key),
        },
        {
          view:
            hasPermission(UserPermission.MENU_HOME_GEOZONE_VISIT_LIST) &&
            hasFeature(FeatureKey.geozone_visit),
          to: "/geozonevisit_list",
          layout: "",
          name: translate(LanguageKey.featurecategory_geozone_visits_name),
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.sidebar_assets_utilization_key),
      state: "openUtilization",
      icon: <span className="utilization-active-menu-icon menu-icon"></span>,
      views: [
        {
          view:
            organisation?.organisationType !== "SYSTEM" &&
            hasFeature(FeatureKey.utilization_dashboards) &&
            hasPermission(UserPermission.MENU_HOME_DEVICE_UTILIZATION_KPI_LIST),
          to: "/utilization",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_DEVICE_UTILIZATION_KPI_LIST_description_key),
          subPages: ["/utilization_graph_and_list"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.custom_data_key),
      state: "openCustomData",
      icon: <span className="custom-data-menu-icon menu-icon"></span>,
      view: hasPermission(UserPermission.MENU_HOME_CUSTOM_DATA_LIST),
      views: [],
    },
    {
      collapse: true,
      title: translate(LanguageKey.manage_processes_key),
      name: translate(LanguageKey.manage_processes_key),
      state: "openProcesses",
      icon: <span className="flow-circle-menu-icon menu-icon"></span>,
      view: organisation?.organisationType !== "SYSTEM",
      views: [
        {
          view:
            hasFeature(FeatureKey.process_manager) &&
            hasPermission(UserPermission.MENU_HOME_PROCESS_LIST),
          to: "/processes",
          layout: "",
          name: translate(LanguageKey.process_list_key),
          subPages: ["/process_statistics"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.maintenance_key),
      state: "openMaintenances",
      icon: <span className="maintenance-menu-icon menu-icon"></span>,
      view: organisation?.organisationType !== "SYSTEM",
      views: [
        {
          view:
            hasFeature(FeatureKey.maintenance_basic) &&
            hasPermission(UserPermission.MENU_HOME_MAINTENANCE_OVERVIEW),
          to: "/maintenance_dashboard",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_MAINTENANCE_OVERVIEW_description_key),
        },
        {
          view:
            hasFeature(FeatureKey.maintenance_basic) &&
            hasPermission(UserPermission.MENU_HOME_MAINTENANCE_PROGRAMS),
          to: "/maintenance_programs_stats",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_MAINTENANCE_PROGRAMS_description_key),
          subPages: [
            "/maintenance_programs_details",
            "/start_maintenance_program",
          ],
        },
        {
          view:
            hasFeature(FeatureKey.maintenance_basic) &&
            hasPermission(UserPermission.MENU_HOME_UPCOMING_MAINTENANCE),
          to: "/maintenance_upcoming",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_UPCOMING_MAINTENANCE_description_key),
        },
        {
          view:
            hasFeature(FeatureKey.maintenance_basic) &&
            hasPermission(UserPermission.MENU_HOME_DONE_MAINTENANCE),
          to: "/maintenance_done",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_DONE_MAINTENANCE_description_key),
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.alerts_key),
      state: "openAlerts",
      icon: <span className="alerts-menu-icon menu-icon"></span>,
      view: organisation?.organisationType !== "SYSTEM",
      views: [
        {
          view:
            hasFeature(FeatureKey.alert_activedb) &&
            hasPermission(UserPermission.MENU_HOME_ALERT_ACTIVE_DASHBOARD),
          to: "/active_alerts_dashboard",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_ALERT_ACTIVE_DASHBOARD_description_key),
        },
        {
          view:
            hasFeature(FeatureKey.alert_activelist) &&
            hasPermission(UserPermission.MENU_HOME_ALERT_ACTIVE_LIST),
          to: "/active_alerts_list",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_ALERT_ACTIVE_LIST_description_key),
        },
        {
          view:
            hasFeature(FeatureKey.alert_histdb) &&
            hasPermission(UserPermission.MENU_HOME_ALERT_HISTORICAL_DASHBOARD),
          to: "/historical_alerts_dashboard",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_ALERT_HISTORICAL_DASHBOARD_description_key),
        },
        {
          view:
            hasFeature(FeatureKey.alert_histlist) &&
            hasPermission(UserPermission.MENU_HOME_ALERT_HISTORICAL_LIST),
          to: "/historical_alerts_list",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_ALERT_HISTORICAL_LIST_description_key),
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.reports_key),
      state: "openReports",
      icon: <span className="report-menu-icon menu-icon"></span>,
      views: [
        {
          view:
            hasFeature(FeatureKey.report_generator) &&
            hasPermission(UserPermission.MENU_HOME_REPORT_LIST),
          to: "/reports",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_REPORT_LIST_description_key),
          subPages: ["/create_report"],
        },
        {
          view:
            hasFeature(FeatureKey.report_generator) &&
            hasPermission(UserPermission.MENU_HOME_REPORT_SCHEDULES),
          to: "/report_schedule",
          layout: "",
          name:
            translate(LanguageKey.UserPermission_MENU_HOME_REPORT_SCHEDULES_description_key),
        },
      ],
    },
  ];
  const [routes, setRoutes] = useState<ExtendedMenuRoute[]>(initialRoutes);
  const [firstInit, setFirstInit] = useState(true);
  const [data, setData] = useState<HomeMenuData>({
    type: MenuTypeConst.MAIN,
    loading: true,
    routes: [],
    availableMenuPaths: {},
    allPaths: {},
    defaultPath: "/tracked_assets",
  });

  const availibleMenuData = useAvailibleMenu(routes, "/tracked_assets");

  useEffect(() => {
    if (firstInit) return;
    // Dashboard/agent/channel/site-map menu items load asynchronously and are
    // appended to `routes` after the first render. `availibleMenuData` is recomputed
    // one render later, so when `firstInit` flips it can still reflect the stale
    // pre-async routes (empty `availableMenuPaths` + a `/tracked_assets` `defaultPath`).
    // Applying that prematurely flips `loading` to false against an inconsistent menu,
    // which makes the post-login landing redirect bounce a dashboard-only user to
    // /no_access instead of their first available menu (STIC-15267). Only apply once
    // the computed menu reflects the current routes.
    if (availibleMenuData.routes !== routes) return;
    setData({ ...data, ...availibleMenuData, loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availibleMenuData, firstInit]);

  useEffect(() => {
    setData({ ...data, loading: true });
    const dashboardViews: ExtendedMenuRoute[] = [
      {
        view: hasFeature(FeatureKey.utilization_suez),
        to: "/suez_dashboard",
        layout: "",
        name: translate(LanguageKey.suez_dashboard_key),
        category: "dashboard",
      },
    ];

    if (hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.DASHBOARD_VIEW)) {
      biClient
        .getDashBoardsForMenu({})
        .then((response) => {
          const dashboards = response.data as ExtendedDashboardMenuItem[];

          for (let i = 0; i < dashboards.length; i++) {
            dashboardViews.push({
              view: hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_HOME_DASHBOARD),
              to: "/bi_dashboard/" + dashboards[i].id,
              layout: "",
              type: "bi",
              name: dashboards[i].menuName || dashboards[i].name,
              reportType: dashboards[i].powerBiReportType,
              category: "dashboard",
            });
          }
          updateDashBoardViews(dashboardViews);
        })
        .catch(() => {
          updateDashBoardViews(dashboardViews);
        });
    } else {
      updateDashBoardViews(dashboardViews);
    }

    // Fetch menu agents (favouriteForAll OR user-favourited) for the Agents section
    if (
      hasFeature(FeatureKey.agents) &&
      hasPermission(UserPermission.AGENT_VIEW) &&
      organisation?.id
    ) {
      agentClient
        .getMenuAgentsForOrg(organisation.id)
        .then((response) => {
          const agents = (response.data || []) as EnrolledAgent[];
          updateAgentViews(agents);
        })
        .catch(() => {
          updateAgentViews([]);
        });
    } else {
      updateAgentViews([]);
    }

    // Fetch channels for custom data menu
    if (hasPermission(UserPermission.MENU_HOME_CUSTOM_DATA_LIST)) {
      channelClient
        .findByOrg(organisation?.id || 0)
        .then((response) => {
          const channels = response.data as ExtendedChannel[];
          updateCustomDataViews(channels);
        })
        .catch(() => {
          updateCustomDataViews([]);
        });
    } else {
      updateCustomDataViews([]);
    }

    // Fetch site maps for home menu
    if (hasFeature(FeatureKey.loc_site_map) && hasPermission(UserPermission.MENU_HOME_SITE_MAP)) {
      sitemapAPI
        .getByOrgCheckVisibility({ orgId: organisation?.id })
        .then((response) => {
          const siteMaps = (response.data?.data || []) as ExtendedSiteMapItem[];
          updateSiteMapViews(siteMaps);
        })
        .catch(() => {
          updateSiteMapViews([]);
        });
    } else {
      updateSiteMapViews([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [global.reloadSidebar]);

  useEffect(() => {
    setSideBarColors(custumInfo);
  }, [custumInfo]);

  function updateDashBoardViews(dashboardViews: ExtendedMenuRoute[]): void {
    dashboardViews.push({
      view: hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_HOME_DASHBOARD),
      to: "/all_dashboards",
      layout: "",
      type: "bi",
      name: translate(LanguageKey.all_dashboards_key),
      category: "dashboard",
    });
    setRoutes((oldRoutes) => {
      const route = oldRoutes.find((x) => x.state === "openDashboards");
      if (route) {
        route.views = [];
        route.views.push(...dashboardViews);
      }
      return [...oldRoutes];
    });
    setFirstInit(false);
    onMenuChange();
  }

  function updateAgentViews(agents: EnrolledAgent[]): void {
    const agentViews: ExtendedMenuRoute[] = [];

    for (let i = 0; i < agents.length; i++) {
      const agent = agents[i];
      agentViews.push({
        view: hasFeature(FeatureKey.agents) && hasPermission(UserPermission.MENU_HOME_AGENT),
        to: "/agents/" + encodeURIComponent(agent.name),
        layout: "",
        type: "agent",
        name: agent.title || agent.name,
        category: "agent",
      });
    }

    // Always show "All agents" as the last entry — mirrors "All dashboards" pattern.
    agentViews.push({
      view: hasFeature(FeatureKey.agents) && hasPermission(UserPermission.MENU_HOME_AGENT),
      to: "/all_agents",
      layout: "",
      type: "agent",
      name: translate(LanguageKey.all_agents_key),
      category: "agent",
    });

    setRoutes((oldRoutes) => {
      const route = oldRoutes.find((x) => x.state === "openAgents");
      if (route) {
        route.views = [];
        route.views.push(...agentViews);
      }
      return [...oldRoutes];
    });
    onMenuChange();
  }

  function updateCustomDataViews(channels: ExtendedChannel[]): void {
    const customDataViews: ExtendedMenuRoute[] = [];

    // Add a menu item for each channel that has showInHomeMenu enabled
    const visibleChannels = channels.filter(
      (channel) => channel.showInHomeMenu
    );

    for (let i = 0; i < visibleChannels.length; i++) {
      const channel = visibleChannels[i];
      customDataViews.push({
        view: hasPermission(UserPermission.MENU_HOME_CUSTOM_DATA_LIST),
        to: "/custom_data_channel/" + channel.id,
        layout: "",
        type: "channel",
        name: channel.homeMenuItemName || channel.displayName || channel.name,
        channelId: channel.id,
        category: "customData",
      });
    }

    setRoutes((oldRoutes) => {
      // Remove Custom Data section if no visible channels, otherwise update views
      if (visibleChannels.length === 0) {
        return oldRoutes.filter((x) => x.state !== "openCustomData");
      } else {
        const route = oldRoutes.find((x) => x.state === "openCustomData");
        if (route) {
          route.views = [];
          route.views.push(...customDataViews);
        }
        return [...oldRoutes];
      }
    });
    onMenuChange();
  }

  function updateSiteMapViews(siteMaps: ExtendedSiteMapItem[]): void {
    setRoutes((oldRoutes) => {
      // If no site maps exist, remove the entire Site maps section
      if (siteMaps.length === 0) {
        return oldRoutes.filter((x) => x.state !== "openSiteMaps");
      }

      const siteMapViews: ExtendedMenuRoute[] = [];

      // Add menu items for site maps with showInHomeMenu enabled (above "All site maps")
      const visibleSiteMaps = siteMaps.filter(
        (siteMap) => siteMap.showInHomeMenu
      );

      for (let i = 0; i < visibleSiteMaps.length; i++) {
        const siteMap = visibleSiteMaps[i];
        siteMapViews.push({
          view: hasPermission(UserPermission.MENU_HOME_SITE_MAP),
          to: "/site_map_view/" + siteMap.id,
          layout: "",
          type: "sitemap",
          name: siteMap.homeMenuItemName || siteMap.name,
          siteMapId: siteMap.id,
          category: "siteMap",
        });
      }

      // Add "All site maps" at the end
      // excludeSubPages prevents "All site maps" from being highlighted when viewing
      // a site map that has its own menu item in the home menu
      siteMapViews.push({
        view:
          hasFeature(FeatureKey.loc_site_map) && hasPermission(UserPermission.MENU_HOME_SITE_MAP),
        to: "/all_site_maps",
        layout: "",
        name: translate(LanguageKey.all_site_maps_key),
        subPages: ["/site_map_view"],
        excludeSubPages: visibleSiteMaps.map((sm) => "/site_map_view/" + sm.id),
      });

      const route = oldRoutes.find((x) => x.state === "openSiteMaps");
      if (route) {
        route.views = [];
        route.views.push(...siteMapViews);
      }
      return [...oldRoutes];
    });
    onMenuChange();
  }

  return data;
};

export default useHomeMenu;
